// src/components/Header.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectMsg } from "../../store/authSlice";

export default function Header({ userData }) {
  const messages = useSelector(selectMsg);
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 288 });

  // 解析 URL
  const searchParams = new URLSearchParams(location.search);
  const layer = searchParams.get("layer");

  // 根據 pathname 決定預設文字
  const pathTagMap = {
    "/statistics": "能耗統計分析",
    "/users": "客戶資訊",
    "/setting": "設定頁面",
  };

  const defaultTag = pathTagMap[location.pathname] || "整體用電(台電)";
  const hasMessages = Array.isArray(messages) && messages.length > 0;
  
  // 警示訊息設定
  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 288;
    const gutter = 16;
    const left = Math.min(
      window.innerWidth - dropdownWidth - gutter,
      Math.max(gutter, rect.right - dropdownWidth)
    );
    const top = rect.bottom + 12;
    setDropdownPosition({ top, left, width: dropdownWidth });
  }, []);

  useEffect(() => {
    if (!hasMessages && showNotifications) {
      setShowNotifications(false);
    }
  }, [hasMessages, showNotifications]);

  useEffect(() => {
    if (!showNotifications) return;
    updateDropdownPosition();

    const handleWindowChange = () => updateDropdownPosition();
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [showNotifications, messages?.length, updateDropdownPosition]);

  useEffect(() => {
    if (!showNotifications) return;
    const handleClickOutside = (event) => {
      if (
        buttonRef.current?.contains(event.target) ||
        dropdownRef.current?.contains(event.target)
      ) {
        return;
      }
      setShowNotifications(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  // 判斷是否在首頁
  const isHomePage = location.pathname === "/home";

  const dropdownContent =
    showNotifications &&
    createPortal(
      <div
        ref={dropdownRef}
        className="fixed z-[999] max-h-80 w-72 overflow-y-auto rounded-xl border border-[#D9CBFF]/40 bg-[#2F293E] p-4 shadow-lg"
        style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width }}
      >
        {hasMessages ? (
          <ul className="flex flex-col gap-3 text-sm">
            {messages.map((item, index) => (
              <li
                key={item?.code || index}
                className="rounded-lg border border-white/10 bg-white/5 p-3 text-left"
              >
                <p className="font-semibold text-white/90">{item?.msg || ""}</p>
                {item?.timestamp && (
                  <span className="mt-1 block text-xs text-white/60">
                    {new Date(item.timestamp).toLocaleString()}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-center text-sm text-white/70">目前沒有警示訊息</p>
        )}
      </div>,
      document.body
    );

  return (
    <>
      {userData && (
        <header className="relative z-[200] flex items-center gap-3 justify-between px-6 p-4 text-white overflow-x-auto min-h-[72px]">
          <div className="flex items-center gap-8">
            <button className="w-8 h-8 hover:opacity-65 flex items-center justify-center transition-colors">
              <img
                src="/assets/icons/arrow.svg"
                alt="Back"
                className="w-6 h-6 rotate-90 object-cover"
              />
            </button>

            <div className="relative z-[250]">
              <button
                type="button"
                className="bg-[#2F293E] hover:bg-[#3B3152] border border-[#D9CBFF]/40 relative rounded-full w-10 h-10 lg:w-[47px] lg:h-[47px] flex items-center justify-center transition-colors"
                ref={buttonRef}
                onClick={() => {
                  if (!hasMessages && !showNotifications) return;
                  setShowNotifications((prev) => !prev);
                }}
              >
                {hasMessages && (
                  <div className="absolute top-[2px] right-1 w-[10px] h-[10px]">
                    <span className="w-full h-full absolute rounded-full bg-red-400 opacity-75 animate-ping"></span>
                    <span className="w-full h-full absolute rounded-full bg-red-500"></span>
                  </div>
                )}
                <img
                  src="/assets/icons/bell.svg"
                  alt="Notifications"
                  className="w-[21px] h-[22px] object-cover"
                />
              </button>
            </div>

            {userData.title && (
              <p className="hidden lg:flex text-white text-[20px] lg:text-[32px]">
                {`Hello, 歡迎 ${userData?.title || ''}`}
              </p>
            )}
          </div>

          {/* 最右邊 Tag: 顯示 Layer 或預設，首頁時隱藏 */}
          {!isHomePage && (
            <button className="bg-[#834189] min-w-fit hover:opacity-65 text-white rounded-[12px] px-4 py-2 h-[40px] font-semibold transition-colors">
              {layer || defaultTag}
            </button>
          )}
        </header>
      )}
      {dropdownContent}
    </>
  );
}
